import { ChangeDetectionStrategy, Component, input, model, OnDestroy, output, viewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { FormValueControl } from '@angular/forms/signals';

import { NgSelectComponent } from '@ng-select/ng-select';

import { BarI18nPipe } from '../i18n/bar-i18n.pipe';
import { BarListItem } from '../shared/bar-list-item.type';
import { BaasDropdownTooltip } from './baas-dropdown-tooltip';

@Component({
    selector: 'baas-select-list',
    templateUrl: './baas-select-list.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FormsModule, NgSelectComponent, BarI18nPipe, BaasDropdownTooltip],
    host: {
        '(window:resize)': 'onViewportResize()'
    }
})
/** Default simple select list component using <ng-select> working with BarListItem. */
export class BaasSelectList implements FormValueControl<any>, OnDestroy {
    readonly ngSelect = viewChild.required(NgSelectComponent);

    readonly value = model<any>();

    /** Bound automatically by the Field directive (signal forms) or set manually (reactive forms). */
    readonly invalid = input(false);
    readonly touched = input(false);
    readonly dirty = input(false);
    readonly pending = input(false);
    readonly disabled = input(false);
    readonly required = input(false);
    readonly items = input.required<BarListItem[]>();
    readonly multiple = input<boolean>();
    readonly checkboxMultiSelection = input(false);
    readonly maxSelectedItems = input<number>();
    readonly labelForId = input<string>();
    readonly clearable = input(true);
    readonly grouping = input<boolean>();
    readonly selectableGroup = input<boolean>(true);
    readonly closeOnSelect = input(true);
    readonly searchable = input(true);
    readonly editableSearchTerm = input(false);
    readonly readonly = input(false);
    readonly virtualScroll = input(false);
    /** Whether to skip focusing the clear action when navigating with Tab. */
    readonly skipClearActionTabFocus = input(false);

    // eslint-disable-next-line @angular-eslint/no-output-native
    readonly change = output<any>();
    // eslint-disable-next-line @angular-eslint/no-output-native
    readonly search = output<{
        term: string;
        items: any[];
    }>();

    #resizeFrame: number | undefined;

    ngOnDestroy(): void {
        // Prevent the scheduled callback from accessing a destroyed view.
        if (this.#resizeFrame !== undefined) cancelAnimationFrame(this.#resizeFrame);
    }

    focus(): void {
        // timeout needed to have effect in certain situations (ngAfterViewInit)
        requestAnimationFrame(() => this.ngSelect().searchInput().nativeElement.focus());
    }

    protected onViewportResize(): void {
        // Coalesce resize events and measure after zoom-triggered layout changes.
        if (this.#resizeFrame !== undefined) {
            return;
        }

        this.#resizeFrame = requestAnimationFrame(() => {
            this.#resizeFrame = undefined;
            const select = this.ngSelect();
            const panel = select.dropdownPanel();
            if (!panel) {
                return;
            }

            const document = select.element.ownerDocument;
            const dropdown = document.getElementById(select.dropdownId)?.closest('ng-dropdown-panel');
            if (!(dropdown instanceof HTMLElement)) {
                return;
            }

            // The panel is appended to body, so align it using body-relative coordinates.
            const selectRect = select.element.getBoundingClientRect();
            const parentRect = document.body.getBoundingClientRect();
            const isRTL = document.documentElement.dir === 'rtl';
            dropdown.style.left = isRTL ? 'auto' : `${selectRect.left - parentRect.left}px`;
            dropdown.style.right = isRTL ? `${parentRect.right - selectRect.right}px` : 'auto';
            dropdown.style.width = `${selectRect.width}px`;
            dropdown.style.minWidth = `${selectRect.width}px`;
            // The public API handles vertical alignment only; width and RTL alignment are set above.
            panel.adjustPosition();
        });
    }
}
